import React, { useState, useEffect } from 'react';
import { AppHeader } from '@/components/AppHeader';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

export default function Privacy() {
  const [user, setUser] = useState<User | null>(null);
  useNavigationHistory(); // Track navigation

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onSignOut={handleSignOut} />

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <p className="text-sm text-muted-foreground mb-8">Last Updated: January 2025</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Introduction</h2>
            <p className="mb-4">
              This Privacy Policy explains how Therappi Ltd ("we", "us", or "our") collects, uses, stores, and protects your 
              personal information when you use our mental health and wellness application ("Service"). We are committed to 
              protecting your privacy and handling your data transparently and securely.
            </p>
            <div className="bg-muted/30 rounded-lg p-4 mb-4">
              <p className="font-semibold">Data Controller:</p>
              <p>Therappi Ltd</p>
              <p>Thames House, 77-77a High Street</p>
              <p>Esher, Surrey, KT10 9PA, United Kingdom</p>
              <p>Email: support@therappi.app</p>
              <p className="mt-2 text-sm">Data Protection Officer: support@therappi.app</p>
            </div>
            <p className="mb-4">
              <strong>International Data Transfers:</strong> As a global service, your information may be transferred to, 
              stored, and processed in countries other than your country of residence, including the United States and 
              European Union. We ensure appropriate safeguards are in place for such transfers in compliance with 
              applicable data protection laws including UK GDPR, EU GDPR, and where applicable, CCPA/CPRA.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">1. Information We Collect</h2>
            <p className="mb-4">
              We collect the following types of information from you when you use our Service:
            </p>

            <h3 className="text-lg font-semibold mb-2">1.1 Information You Provide Directly</h3>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">
                <strong>Account Information:</strong> When you create an account, we collect your name, email address,
                password, date of birth, gender, and any other information you choose to provide.
              </li>
              <li className="mb-2">
                <strong>Profile Information:</strong> You may choose to add additional information to your profile, such as
                your profile picture, location, interests, and other personal details.
              </li>
              <li className="mb-2">
                <strong>User Content:</strong> We collect any content you submit to the Service, such as journal entries,
                goals, reflections, and other text, images, or multimedia.
              </li>
              <li className="mb-2">
                <strong>Communications:</strong> We collect information contained in your communications with us, such as
                support requests, feedback, and other inquiries.
              </li>
              <li className="mb-2">
                <strong>Payment Information:</strong> When you make a purchase, we collect your payment information, such
                as your credit card number, billing address, and other payment details. This information is processed
                securely by our payment processor, Stripe, and is not stored on our servers.
              </li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">1.2 Information We Collect Automatically</h3>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">
                <strong>Log Data:</strong> We automatically collect certain information when you use the Service, such as
                your IP address, browser type, operating system, device information, and the pages you visit.
              </li>
              <li className="mb-2">
                <strong>Usage Data:</strong> We collect information about how you use the Service, such as the features you
                use, the content you view, and the time you spend on the Service.
              </li>
              <li className="mb-2">
                <strong>Cookies and Similar Technologies:</strong> We use cookies and similar technologies to collect
                information about your browsing activity. You can control cookies through your browser settings.
              </li>
              <li className="mb-2">
                <strong>Location Data:</strong> With your consent, we may collect your precise location data to provide
                location-based features.
              </li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">1.3 Information from Third-Party Sources & Biometric Data</h3>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">
                <strong>Social Media:</strong> If you connect your account to a social media platform, we may collect
                information from your social media profile, such as your name, profile picture, and friends list.
              </li>
              <li className="mb-2">
                <strong>Service Providers:</strong> We may receive information from our service providers, such as payment
                processors, analytics providers, and advertising partners.
              </li>
              <li className="mb-2">
                <strong>WHOOP Integration (Biometric Health Data):</strong> If you connect your WHOOP account to Therappi, 
                we access all 6 WHOOP API scopes and collect sensitive biometric and health data including:
                <ul className="list-disc pl-6 mt-2">
                  <li><strong>Recovery Scope:</strong> Recovery scores, HRV (Heart Rate Variability), resting heart rate, recovery trends</li>
                  <li><strong>Sleep Scope:</strong> Sleep duration, sleep efficiency, sleep stages (deep sleep, REM, light sleep), sleep disturbances, sleep performance scores</li>
                  <li><strong>Workout Scope:</strong> Daily strain scores, workout details, activity types, heart rate zones, calories burned, active heart rate</li>
                  <li><strong>Cycles Scope:</strong> Physiological cycles data, recovery cycles, strain cycles, performance patterns</li>
                  <li><strong>Profile Scope:</strong> WHOOP user ID, account information, timezone, measurement preferences</li>
                  <li><strong>Body Measurement Scope:</strong> Height, weight, maximum heart rate, body metrics for accurate calculations</li>
                  <li>Timestamps and date ranges for all biometric measurements</li>
                </ul>
                This data is collected only after you provide explicit opt-in consent through our WHOOP authorization dialog. 
                You maintain full control over this data and can download or delete it at any time through Settings → Integrations → WHOOP Data Rights.
              </li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">1.4 Mental Health & Timeline Data (The Sanctuary)</h3>
            <p className="mb-4">
              If you subscribe to The Sanctuary tier, we collect additional sensitive mental health data:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Past Self Timeline:</strong> Life events you record including dates, descriptions, emotional impact, trauma, achievements, relationships, and pivotal moments</li>
              <li className="mb-2"><strong>Mental Health Conditions:</strong> Self-reported diagnoses, symptoms, condition severity, treatment history</li>
              <li className="mb-2"><strong>Symptom Tracking:</strong> Daily mood ratings, anxiety levels, energy levels, sleep quality, and other mental health indicators</li>
              <li className="mb-2"><strong>Condition-Specific Exercises:</strong> Completion status, effectiveness ratings, notes on anxiety/depression/trauma/OCD/stress/grief tools</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">1.5 Crisis Detection & Safety Data</h3>
            <p className="mb-4">
              When Crisis Mode activates due to high-risk language detection, we automatically log:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Trigger Content:</strong> The message content that triggered crisis detection (phrases indicating suicidal ideation, self-harm, severe distress)</li>
              <li className="mb-2"><strong>Risk Assessment:</strong> AI-determined risk level (critical, high, medium, low)</li>
              <li className="mb-2"><strong>Crisis Events:</strong> Timestamps of activation, exit method (user confirmed safety, timeout, manual override), duration in crisis mode</li>
              <li className="mb-2"><strong>Agent Source:</strong> Which feature triggered crisis mode (Therappi AI, Soul Guidance, Dream Analysis)</li>
              <li className="mb-2"><strong>Resolution Data:</strong> How crisis mode was resolved and any follow-up actions taken</li>
            </ul>
            <p className="mb-4">
              Crisis logs are retained for 90 days for safety oversight and may be reviewed by authorised personnel for quality 
              assurance. These logs are stored with the highest security standards and are never shared externally unless required by law.
            </p>

            <div className="bg-muted/30 rounded-lg p-4 mb-4">
              <h4 className="font-semibold mb-2">WHOOP Data Usage & Rights</h4>
              <p className="text-sm mb-2">
                <strong>Purpose:</strong> We use WHOOP data exclusively to enhance your wellness journey by:
              </p>
              <ul className="list-disc pl-6 text-sm mb-2">
                <li>Auto-populating your daily check-ins for convenience</li>
                <li>Providing insights on the mind-body connection</li>
                <li>Correlating physical recovery with mental wellness patterns</li>
                <li>Displaying your biometric data in your personalised dashboard</li>
              </ul>
              <p className="text-sm mb-2">
                <strong>Your Rights:</strong> You can access, download, or delete all your WHOOP data at any time 
                via Settings → Integrations → WHOOP Data Rights. You can disconnect your WHOOP account at any time, 
                and we will cease collecting new data immediately.
              </p>
              <p className="text-sm mb-2">
                <strong>Data Retention:</strong> We retain WHOOP data only while your WHOOP integration is active. 
                When you disconnect, you can choose to delete all stored WHOOP data.
              </p>
              <p className="text-sm mb-2">
                <strong>No Third-Party Sharing:</strong> Your WHOOP data is never sold, rented, or shared with third 
                parties. It is used exclusively within Therappi to provide you with insights.
              </p>
              <p className="text-sm">
                <strong>Security:</strong> All WHOOP data is encrypted in transit (HTTPS) and at rest, complying with 
                GDPR, CCPA, and other data privacy regulations.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">2. AI and Data Use</h2>

            <h3 className="text-lg font-semibold mb-2">2.1 AI-Powered Features & Memory Bank</h3>
            <p className="mb-4">
              Our Service utilises artificial intelligence (AI) to provide personalised mental health and wellness
              support. AI features include:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">
                <strong>AI Therappi<sup className="text-[0.6em]">®</sup>:</strong> An AI-powered conversational tool that provides personalised support,
                insights, and guidance with continuous memory across sessions.
              </li>
              <li className="mb-2">
                <strong>AI Memory Bank:</strong> A system that stores conversation summaries, key topics discussed, emotional 
                patterns, and personalised insights to maintain context across sessions. Memory is shared between AI Therappi, 
                Soul Guidance, and Dream Analysis.
              </li>
              <li className="mb-2">
                <strong>Crisis Detection:</strong> Automated monitoring of conversations for high-risk language patterns 
                (suicidal ideation, self-harm, severe distress) that triggers Crisis Mode with pre-approved safety templates.
              </li>
              <li className="mb-2">
                <strong>Dream Analysis:</strong> AI algorithms analyse your dream journal entries to provide insights into
                your subconscious and emotional patterns.
              </li>
              <li className="mb-2">
                <strong>Personalised Insights:</strong> AI algorithms analyse your usage patterns, mood data, and timeline 
                events to provide personalised insights and recommendations.
              </li>
              <li className="mb-2">
                <strong>Condition-Specific Support:</strong> AI adapts responses based on self-reported mental health conditions 
                and symptom tracking (available with The Sanctuary subscription).
              </li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">2.2 Data Usage for AI</h3>
            <p className="mb-4">
              To power our AI features, we use your data in the following ways:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">
                <strong>Data Analysis:</strong> We analyse your data to identify patterns, trends, and insights that can
                help improve your mental health and wellness.
              </li>
              <li className="mb-2">
                <strong>Model Training:</strong> We use your data to train and improve our AI models.
              </li>
              <li className="mb-2">
                <strong>Personalisation:</strong> We use your data to personalise your experience and provide you with
                relevant content and recommendations.
              </li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">2.3 Data Anonymization and Aggregation</h3>
            <p className="mb-4">
              We may anonymize and aggregate your data to create statistical and analytical reports. These reports do not
              contain any personally identifiable information and are used to improve our Service and for research
              purposes.
            </p>

            <h3 className="text-lg font-semibold mb-2">2.4 AI Conversation Logging & Crisis Monitoring</h3>
            <p className="mb-4">
              <strong>Standard Logging:</strong> All conversations with AI Therappi<sup className="text-[0.6em]">®</sup>, Soul Guidance, and Dream Analysis 
              are logged for safety, quality assurance, crisis detection, and to improve AI performance. Individual messages and 
              conversation summaries are stored securely with encryption.
            </p>
            <p className="mb-4">
              <strong>Crisis Monitoring:</strong> Our AI continuously monitors conversations for high-risk language patterns. 
              When crisis indicators are detected (e.g., suicidal ideation, self-harm, severe distress):
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">The triggering message content is flagged and logged with risk assessment</li>
              <li className="mb-2">Crisis Mode activates automatically, restricting AI to pre-approved safety templates</li>
              <li className="mb-2">Emergency resources are displayed prominently</li>
              <li className="mb-2">All crisis events (activation, duration, exit method) are logged for safety oversight</li>
            </ul>
            <p className="mb-4">
              Crisis logs are retained for 90 days and may be reviewed by authorised personnel for quality assurance and safety 
              governance. They are never shared with third parties unless required by law or to prevent imminent harm.
            </p>

            <h3 className="text-lg font-semibold mb-2">2.5 AI Limitations and Disclaimer</h3>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-4">
              <h4 className="text-lg font-semibold mb-2 text-destructive">
                IMPORTANT: AI IS NOT A SUBSTITUTE FOR PROFESSIONAL CARE
              </h4>
              <p className="mb-2">
                Our AI features are intended for wellness and self-help purposes only. They are not a substitute for
                professional medical advice, diagnosis, or treatment. If you are experiencing a mental health crisis,
                please contact emergency services or a qualified mental health professional immediately.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">
              <strong>Legal Bases for Processing (UK/EU GDPR):</strong> We process your personal data based on:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Contract Performance (Article 6(1)(b)):</strong> To provide the Service you've subscribed to</li>
              <li className="mb-2"><strong>Consent (Article 6(1)(a)):</strong> Where you've given explicit consent (e.g., WHOOP integration, marketing emails, optional data sharing)</li>
              <li className="mb-2"><strong>Legitimate Interests (Article 6(1)(f)):</strong> To improve our Service, prevent fraud, ensure security, and provide crisis detection for user safety</li>
              <li className="mb-2"><strong>Legal Obligation (Article 6(1)(c)):</strong> To comply with applicable laws and regulations</li>
              <li className="mb-2"><strong>Vital Interests (Article 6(1)(d)):</strong> To protect life or prevent serious harm in crisis situations</li>
            </ul>

            <p className="mb-4"><strong>Specific Uses of Your Information:</strong></p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Service Provision:</strong> Create and manage your account, provide personalised wellness features, deliver the Service</li>
              <li className="mb-2"><strong>Payment Processing:</strong> Process transactions via Stripe in compliance with PCI DSS standards</li>
              <li className="mb-2"><strong>AI Features & Memory Bank:</strong> Power AI Therappi<sup className="text-[0.6em]">®</sup> conversations, maintain conversation memory across sessions, provide personalised insights based on your history</li>
              <li className="mb-2"><strong>Crisis Detection & Safety:</strong> Automatically monitor conversations for high-risk language, activate Crisis Mode, log crisis events, provide emergency resources</li>
              <li className="mb-2"><strong>Timeline Integration:</strong> Use Past Self Timeline events to provide deeper, trauma-informed therapeutic support (The Sanctuary only)</li>
              <li className="mb-2"><strong>WHOOP Integration:</strong> Correlate biometric data (recovery, sleep, strain) with mental wellness patterns, auto-populate daily check-ins, provide mind-body insights</li>
              <li className="mb-2"><strong>Communications:</strong> Send service updates, security alerts, crisis follow-up, respond to inquiries, provide customer support</li>
              <li className="mb-2"><strong>Analytics and Improvement:</strong> Analyse usage patterns, understand user behaviour, improve AI accuracy, enhance crisis detection</li>
              <li className="mb-2"><strong>Safety and Security:</strong> Detect fraud, prevent abuse, enforce Terms, protect users, review crisis logs for quality assurance</li>
              <li className="mb-2"><strong>Legal Compliance:</strong> Comply with legal obligations, respond to lawful requests, protect against legal liability</li>
              <li className="mb-2"><strong>Marketing:</strong> Send promotional communications (with your consent, where required by law)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">4. How We Share Your Information</h2>
            <p className="mb-4">
              We may share your information with the following categories of recipients:
            </p>

            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">
                <strong>Service Providers:</strong> We share information with third-party service providers who help us
                operate, maintain, and improve our Service. These service providers include payment processors (Stripe),
                analytics providers, cloud hosting providers, and customer support providers.
              </li>
              <li className="mb-2">
                <strong>Business Partners:</strong> We may share information with business partners who offer products or
                services that may be of interest to you. We will only do this with your consent.
              </li>
              <li className="mb-2">
                <strong>Legal Authorities:</strong> We may disclose information to legal authorities if required by law or
                legal process, or if we believe it is necessary to protect our rights, property, or safety, or the
                rights, property, or safety of others.
              </li>
              <li className="mb-2">
                <strong>Affiliates:</strong> We may share information with our affiliates, which are companies that are
                under common control with us.
              </li>
              <li className="mb-2">
                <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of all or a portion of
                our assets, we may transfer your information to the acquiring company.
              </li>
            </ul>

            <p className="mb-4">
              We require all recipients of your information to protect it in accordance with this Privacy Policy and
              applicable law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">5. Data Security</h2>
            <p className="mb-4">
              We take reasonable measures to protect your information from unauthorised access, use, or disclosure. These
              measures include:
            </p>

            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">
                <strong>Encryption:</strong> We use encryption to protect your information when it is transmitted over the
                internet.
              </li>
              <li className="mb-2">
                <strong>Access Controls:</strong> We restrict access to your information to authorised personnel only.
              </li>
              <li className="mb-2">
                <strong>Security Monitoring:</strong> We monitor our systems for security vulnerabilities and attacks.
              </li>
              <li className="mb-2">
                <strong>Data Minimization:</strong> We only collect and retain the information that is necessary for the
                purposes described in this Privacy Policy.
              </li>
              <li className="mb-2">
                <strong>Regular Audits:</strong> We conduct regular security audits to ensure that our security measures
                are effective.
              </li>
            </ul>

            <p className="mb-4">
              Despite our efforts, no security measures are perfect or impenetrable, and we cannot guarantee the security
              of your information. In the event of a security breach, we will notify you as required by applicable law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">6. Data Retention</h2>
            <p className="mb-4">
              We retain your information for as long as necessary to provide you with the Service, to comply with
              applicable law, and to enforce our agreements. Specific retention periods:
            </p>

            <h3 className="text-lg font-semibold mb-2">6.1 Standard Data Retention</h3>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Account Information:</strong> Retained while your account is active and for up to 1 year after account closure for legal compliance</li>
              <li className="mb-2"><strong>Payment Information:</strong> Stored by Stripe for as long as required by law (typically 7 years for tax purposes)</li>
              <li className="mb-2"><strong>AI Conversation History:</strong> Retained indefinitely unless you request deletion via Settings → Privacy → Clear AI Memory</li>
              <li className="mb-2"><strong>Timeline Events & Mental Health Data:</strong> Retained indefinitely unless you request deletion</li>
              <li className="mb-2"><strong>WHOOP Biometric Data:</strong> Retained while WHOOP integration is active; you can delete at any time via Settings → Integrations → WHOOP Data Rights</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">6.2 Crisis & Safety Data Retention</h3>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Crisis Logs:</strong> Retained for 90 days from the date of crisis event, then automatically deleted</li>
              <li className="mb-2"><strong>Risk Flags:</strong> High-risk conversation flags retained for 90 days for safety oversight and quality assurance</li>
              <li className="mb-2"><strong>Safety Plan Data:</strong> Retained indefinitely while your account is active; deleted upon account closure or user request</li>
            </ul>

            <p className="mb-4">
              You may request that we delete your information at any time via support@therappi.app, subject to our legal 
              obligations (e.g., tax records, fraud prevention, legal holds).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">7. Your Privacy Rights</h2>

            <h3 className="text-lg font-semibold mb-2">7.1 UK & EU Users (GDPR Rights)</h3>
            <p className="mb-4">Under UK GDPR and EU GDPR, you have the right to:</p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Right of Access (Article 15):</strong> Request a copy of your personal data we hold</li>
              <li className="mb-2"><strong>Right to Rectification (Article 16):</strong> Correct inaccurate or incomplete data</li>
              <li className="mb-2"><strong>Right to Erasure (Article 17):</strong> Request deletion of your data ("right to be forgotten"), subject to legal retention requirements</li>
              <li className="mb-2"><strong>Right to Restriction (Article 18):</strong> Limit how we process your data in certain circumstances</li>
              <li className="mb-2"><strong>Right to Portability (Article 20):</strong> Receive your data in a structured, machine-readable format</li>
              <li className="mb-2"><strong>Right to Object (Article 21):</strong> Object to processing based on legitimate interests or direct marketing</li>
              <li className="mb-2"><strong>Right to Withdraw Consent (Article 7):</strong> Withdraw consent at any time where processing is based on consent</li>
              <li className="mb-2"><strong>Right to Lodge a Complaint:</strong> File a complaint with the UK Information Commissioner's Office (ico.org.uk) or your local EU data protection authority</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">7.2 California Residents (CCPA/CPRA Rights)</h3>
            <p className="mb-4">Under the California Consumer Privacy Act and California Privacy Rights Act, you have the right to:</p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Right to Know:</strong> Know what personal information we collect, use, disclose, and sell (we do not sell personal information)</li>
              <li className="mb-2"><strong>Right to Delete:</strong> Request deletion of your personal information, subject to certain exceptions</li>
              <li className="mb-2"><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
              <li className="mb-2"><strong>Right to Opt-Out:</strong> Opt-out of the sale or sharing of personal information (note: we do not sell personal information)</li>
              <li className="mb-2"><strong>Right to Limit Sensitive Data:</strong> Limit use of sensitive personal information</li>
              <li className="mb-2"><strong>Right to Non-Discrimination:</strong> Non-discrimination for exercising your privacy rights</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">7.3 Other US State Privacy Laws</h3>
            <p className="mb-4">
              Residents of Virginia (VCDPA), Colorado (CPA), Connecticut (CTDPA), and other US states with comprehensive 
              privacy laws have similar rights including access, deletion, correction, and opt-out rights. Contact us to 
              exercise these rights.
            </p>

            <h3 className="text-lg font-semibold mb-2">7.4 Other International Rights</h3>
            <p className="mb-4">
              Depending on your location, you may have additional rights under:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Canadian Personal Information Protection and Electronic Documents Act (PIPEDA)</li>
              <li className="mb-2">Australian Privacy Principles (APP)</li>
              <li className="mb-2">Brazilian General Data Protection Law (LGPD)</li>
              <li className="mb-2">Other local data protection laws</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">7.5 Exercising Your Rights</h3>
            <p className="mb-4">
              To exercise any of these rights, please contact us at <strong>support@therappi.app</strong> with:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Your full name and account email</li>
              <li className="mb-2">A description of your request</li>
              <li className="mb-2">Proof of identity (to protect your privacy)</li>
            </ul>
            <p className="mb-4">
              We will respond to verified requests within the timeframes required by applicable law:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>UK/EU GDPR:</strong> 30 days (may extend to 60 days for complex requests)</li>
              <li className="mb-2"><strong>CCPA/CPRA:</strong> 45 days (may extend to 90 days)</li>
              <li className="mb-2"><strong>Other jurisdictions:</strong> As required by local law</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">8. Children's Privacy (COPPA & Age-Appropriate Design)</h2>
            <p className="mb-4">
              <strong>Minimum Age:</strong> Our Service is not intended for children under 13 years of age. We do 
              not knowingly collect personal information from children under 13 without verifiable parental consent, 
              in compliance with the Children's Online Privacy Protection Act (COPPA) and UK Age Appropriate Design Code.
            </p>

            <h3 className="text-lg font-semibold mb-2">8.1 Users Aged 13-17 (Minors)</h3>
            <p className="mb-4">
              For users between 13 and 17 years old ("Minors"), we require verifiable parental or legal guardian 
              consent before account creation. We implement additional safeguards for Minor accounts:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2"><strong>Privacy by Default:</strong> Enhanced privacy settings enabled automatically</li>
              <li className="mb-2"><strong>Restricted Features:</strong> Limited community interactions and content access</li>
              <li className="mb-2"><strong>Additional Moderation:</strong> Enhanced content moderation and safety monitoring</li>
              <li className="mb-2"><strong>Minimal Data Collection:</strong> Only collect data necessary for Service provision</li>
              <li className="mb-2"><strong>No Profiling:</strong> No targeted advertising, profiling, or automated decision-making for commercial purposes</li>
              <li className="mb-2"><strong>Data Retention:</strong> Shorter retention periods for Minor data where feasible</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">8.2 Parental Rights and Controls</h3>
            <p className="mb-4">
              Parents or guardians can contact us at <strong>support@therappi.app</strong> to:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">Review the personal information collected from the Minor</li>
              <li className="mb-2">Request deletion of the Minor's account and data</li>
              <li className="mb-2">Refuse further collection or use of the Minor's information</li>
              <li className="mb-2">Modify parental consent settings</li>
              <li className="mb-2">Receive information about our data practices for Minors</li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">8.3 Discovery of Underage Users</h3>
            <p className="mb-4">
              If we learn we have collected personal information from a child under 13 without verified parental 
              consent, or from a Minor aged 13-17 without parental consent, we will delete that information promptly. 
              If you believe we may have information from or about a child, please contact support@therappi.app immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9. Third-Party Links and Services</h2>
            <p className="mb-4">
              Our Service may contain links to third-party websites, applications, and services. We are not responsible
              for the privacy practices of these third parties. We encourage you to review the privacy policies of these
              third parties before providing them with your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">10. International Data Transfers</h2>
            <p className="mb-4">
              As a global service, your information may be transferred to, stored, and processed in countries other than
              your country of residence, including the United States and European Union. These countries may have data
              protection laws that are different from the laws of your country.
            </p>

            <p className="mb-4">
              We ensure that appropriate safeguards are in place to protect your information when it is transferred
              internationally. These safeguards include:
            </p>

            <ul className="list-disc pl-6 mb-4">
              <li className="mb-2">
                <strong>Standard Contractual Clauses (SCCs):</strong> We use Standard Contractual Clauses approved by the
                European Commission (Decision 2021/914) to ensure that your information is adequately protected when it is transferred to countries
                outside the European Economic Area. SCCs are legally binding contractual commitments between data exporters and importers
                that ensure GDPR-level data protection regardless of the recipient country's data protection laws.
              </li>
              <li className="mb-2">
                <strong>UK International Data Transfer Agreement (IDTA):</strong> For transfers from the UK, we use the UK IDTA 
                as approved by the UK Information Commissioner's Office to ensure continued protection of UK personal data.
              </li>
              <li className="mb-2">
                <strong>Adequacy Decisions:</strong> Where the European Commission or UK government has determined that a recipient 
                country ensures adequate data protection (e.g., EEA countries, certain other jurisdictions), we rely on these adequacy decisions.
              </li>
              <li className="mb-2">
                <strong>NOTE:</strong> We do NOT rely on the EU-US Privacy Shield or Swiss-US Privacy Shield Frameworks, as these were 
                invalidated by the Court of Justice of the European Union in Schrems II (Case C-311/18) in July 2020. Any previous 
                references to Privacy Shield are superseded by our use of Standard Contractual Clauses.
              </li>
            </ul>

            <h3 className="text-lg font-semibold mb-2">10.1 Subprocessors & Third-Party Service Providers</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">List of Data Subprocessors:</p>
              <p className="mb-4">
                We engage the following third-party subprocessors who may process your personal data on our behalf:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Supabase (Database & Backend Infrastructure):</strong> Cloud database hosting, authentication, storage. Data location: US/EU (user choice). Compliance: GDPR, SOC 2, ISO 27001.</li>
                <li><strong>Anthropic (AI Provider - Claude Models):</strong> AI conversation processing. Data location: US. Compliance: SOC 2, does not train on enterprise customer data.</li>
                <li><strong>Google (AI Provider - Gemini Models):</strong> AI conversation processing (secondary/backup). Data location: US/Global. Compliance: GDPR, ISO 27001, SOC 2/3.</li>
                <li><strong>Stripe (Payment Processing):</strong> Payment card processing, billing. Data location: US/EU. Compliance: PCI DSS Level 1, GDPR.</li>
                <li><strong>WHOOP (Biometric Data Provider):</strong> Biometric health data API integration. Data location: US. Compliance: HIPAA (for healthcare clients), GDPR.</li>
                <li><strong>Resend / Email Service Provider:</strong> Transactional email delivery. Data location: US. Compliance: GDPR.</li>
              </ul>
              <p className="mb-4 font-semibold">
                Standard Contractual Clauses (SCCs) are in place with all subprocessors located outside the EEA/UK to ensure GDPR-compliant 
                data transfers. We conduct due diligence on all subprocessors to ensure appropriate security measures.
              </p>
              <p className="mb-4">
                <strong>Changes to Subprocessors:</strong> We will notify you at least 30 days in advance of adding or replacing any 
                subprocessors via email. If you object to a new subprocessor, you may terminate your account within the notification period.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">10.2 Data Breach Notification Timeline</h3>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-destructive">Data Breach Response Protocol:</p>
              <p className="mb-4">
                In the event of a personal data breach (unauthorised access, loss, disclosure, alteration, or destruction of your personal data), 
                we will:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Internal Detection & Assessment:</strong> Identify and assess the breach within 24 hours of discovery</li>
                <li><strong>Supervisory Authority Notification (GDPR/UK GDPR):</strong> Notify the relevant data protection authority 
                (UK ICO, relevant EU DPA) within 72 hours of becoming aware of the breach, unless the breach is unlikely to result in risk 
                to your rights and freedoms</li>
                <li><strong>User Notification Timeline:</strong>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>High-Risk Breaches:</strong> Notify affected users within 72 hours if the breach is likely to result in 
                    high risk to your rights (e.g., exposure of sensitive mental health data, biometric data, crisis logs)</li>
                    <li><strong>Standard Breaches:</strong> Notify affected users within 7 days for breaches that pose some risk but not 
                    high risk (e.g., email address exposure, limited account information)</li>
                    <li><strong>Low-Risk Breaches:</strong> No individual notification required, but breach details included in annual transparency report</li>
                  </ul>
                </li>
                <li><strong>Notification Method:</strong> Via email to your registered email address and in-app notification</li>
                <li><strong>Information Provided:</strong> Nature of breach, data affected, potential consequences, mitigation steps we've taken, 
                steps you should take to protect yourself</li>
              </ul>
              <p className="mb-4 font-semibold">
                <strong>US State Laws:</strong> For residents of California (CCPA), Virginia (VCDPA), Colorado (CPA), Connecticut (CTDPA), 
                and other US states with breach notification laws, we will comply with state-specific notification timelines, typically 
                within 30-60 days depending on jurisdiction.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">10.3 AI Training & Automated Decision-Making</h2>
            
            <h3 className="text-lg font-semibold mb-2">Right to Withdraw from AI Training</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">How Your Data Is Used for AI Training:</p>
              <p className="mb-4">
                We use anonymized, aggregated conversation data to improve our internal AI safety systems, crisis detection algorithms, 
                and user experience. Specifically:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Internal AI Improvement:</strong> Anonymized conversation patterns help us identify common user needs, improve 
                crisis detection accuracy, and enhance AI response quality</li>
                <li><strong>No Third-Party AI Training:</strong> Your data is NOT used to train third-party AI models (Anthropic Claude, 
                Google Gemini). Both providers have enterprise agreements confirming they do not train on customer data submitted via their APIs</li>
                <li><strong>Anonymization Process:</strong> Before use for internal AI training, all personally identifiable information 
                (name, email, account IDs, specific life events) is removed. Only anonymized patterns and aggregated statistics are used</li>
              </ul>
              <p className="mb-4 font-semibold">Your Right to Opt-Out:</p>
              <p className="mb-4">
                <strong>You can withdraw consent for AI training at any time</strong> via Settings → Privacy → AI Training Preferences → 
                "Do Not Use My Data for AI Training". This will:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Prevent future conversations from being used for internal AI training</li>
                <li>Not affect your ability to use AI features (Therappi AI, Dream Analysis, Soul Guidance)</li>
                <li>Not remove previously anonymized data from training datasets (as it cannot be re-identified)</li>
              </ul>
              <p className="mb-4">
                <strong>GDPR Rights:</strong> Under GDPR Article 21, you have the right to object to processing for legitimate interests, 
                including AI training. Exercising this right will not impact your Service access.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">Automated Decision-Making & Profiling (GDPR Article 22)</h3>
            <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-primary">GDPR Article 22 Disclosure:</p>
              <p className="mb-4">
                Under GDPR Article 22, you have the right not to be subject to decisions based solely on automated processing (including profiling) 
                that produce legal effects or similarly significantly affect you.
              </p>
              <p className="mb-4 font-semibold">Automated Decisions We Make:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Crisis Mode Activation (Automated):</strong> Our AI automatically detects high-risk language and triggers Crisis Mode. 
                This restricts AI responses to pre-approved safety templates. <strong>This is for your safety and does not significantly affect you 
                legally or economically.</strong> You can manually exit Crisis Mode once you confirm safety.</li>
                <li><strong>Content Recommendations (Automated):</strong> We use automated algorithms to recommend coping tools, exercises, and 
                content based on your usage patterns. <strong>These are suggestions only and do not significantly affect you.</strong></li>
                <li><strong>Subscription Tier Access (Automated):</strong> Your subscription tier is automatically determined by your payment 
                status, which gates access to certain features. <strong>This is based on your explicit subscription choice.</strong></li>
              </ul>
              <p className="mb-4 font-semibold">Decisions NOT Made Solely by Automation:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Account Termination:</strong> Always involves human review before final decision</li>
                <li><strong>Refund/Dispute Resolution:</strong> Handled by human support team</li>
                <li><strong>Content Moderation:</strong> AI flags content, but final decisions involve human review</li>
              </ul>
              <p className="mb-4">
                <strong>Your Rights (EU/UK Users):</strong> You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Request human intervention in automated decisions (contact support@therappi.app)</li>
                <li>Express your point of view regarding automated decisions</li>
                <li>Contest automated decisions and request human review</li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold mb-2">Data Portability Format Specifications (GDPR Article 20)</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">Exercising Your Right to Data Portability:</p>
              <p className="mb-4">
                Under GDPR Article 20, you have the right to receive your personal data in a structured, commonly used, and machine-readable format 
                and to transmit that data to another controller.
              </p>
              <p className="mb-4 font-semibold">Available Export Formats:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>JSON Format (Primary):</strong> All personal data exported in JSON (JavaScript Object Notation), a widely-used, 
                structured, machine-readable format compatible with most data systems</li>
                <li><strong>CSV Format:</strong> Tabular data (check-ins, mood logs, timeline events, WHOOP metrics) available in CSV 
                (Comma-Separated Values) for easy import into spreadsheet applications</li>
                <li><strong>PDF Format:</strong> Human-readable PDF reports for journal entries, safety plans, and personal documents</li>
              </ul>
              <p className="mb-4 font-semibold">How to Export Your Data:</p>
              <ol className="list-decimal pl-6 space-y-2 mb-4">
                <li>Navigate to Settings → Privacy → Data Portability → "Request Data Export"</li>
                <li>Select desired format (JSON, CSV, or PDF)</li>
                <li>Select data categories to export (conversations, timeline, WHOOP data, etc.)</li>
                <li>Receive download link via email within 48 hours (typically within 24 hours)</li>
                <li>Download encrypted ZIP file (password sent separately for security)</li>
              </ol>
              <p className="mb-4 font-semibold">What Data Is Included:</p>
              <p className="mb-4">
                Your export will include: Account information, AI conversation history, timeline events, mental health condition tracking, 
                daily check-ins, WHOOP biometric data, journal entries, safety plans, symptom logs, and all user-generated content.
              </p>
              <p className="mb-4">
                <strong>Note:</strong> Anonymized data used in aggregated analytics cannot be exported as it cannot be re-identified to you.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">10.4 US State Mental Health Records Laws & COPPA Compliance</h2>
            
            <h3 className="text-lg font-semibold mb-2">US State Mental Health Records Laws</h3>
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold">State-Specific Mental Health Data Protections:</p>
              <p className="mb-4">
                Several US states have enhanced privacy protections for mental health records beyond HIPAA (which does not apply to Therappi 
                as we are not a healthcare provider). We comply with applicable state laws including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>California (CMIA - Confidentiality of Medical Information Act):</strong> Mental health information requires 
                explicit authorization for disclosure. We do not disclose California residents' mental health data without consent except 
                as required by law.</li>
                <li><strong>New York (Mental Hygiene Law § 33.13):</strong> Psychiatric and psychological records are confidential and 
                require patient consent for release. We protect New York residents' mental health data accordingly.</li>
                <li><strong>Illinois (Mental Health and Developmental Disabilities Confidentiality Act):</strong> Mental health records 
                are strictly confidential. We do not disclose Illinois residents' mental health data without consent.</li>
                <li><strong>Texas (Health & Safety Code § 611):</strong> Mental health information is confidential and requires patient 
                authorization for disclosure.</li>
                <li><strong>Massachusetts (MGL c. 112, § 129A):</strong> Psychotherapist-patient privilege protections (though we are 
                not psychotherapists, we apply similar confidentiality standards).</li>
              </ul>
              <p className="mb-4 font-semibold">
                <strong>Important Clarification:</strong> While we comply with state mental health records laws, Therappi is NOT a healthcare 
                provider and does not create "medical records" or "healthcare records" under HIPAA or state healthcare laws. Mental health data 
                you provide is protected under general data protection laws (CCPA, state privacy laws) and our contractual privacy obligations.
              </p>
            </div>

            <h3 className="text-lg font-semibold mb-2">COPPA Verifiable Parental Consent Mechanism</h3>
            <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 mb-4">
              <p className="mb-4 font-semibold text-primary">Children's Online Privacy Protection Act (COPPA) Compliance:</p>
              <p className="mb-4">
                <strong>Age Requirement:</strong> Therappi requires users to be at least 13 years old. Users aged 13-17 require verifiable 
                parental consent before account creation in compliance with COPPA (15 U.S.C. §§ 6501-6506).
              </p>
              <p className="mb-4 font-semibold">Verifiable Parental Consent Methods:</p>
              <p className="mb-4">
                For users aged 13-17, we obtain verifiable parental consent using one of the following FTC-approved methods:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Email Plus (Primary Method):</strong> Parent provides email address, receives consent notification email with 
                detailed information about data collection, and must click confirmation link AND respond with explicit consent statement 
                ("I consent to my child using Therappi") from the same email address</li>
                <li><strong>Payment Verification:</strong> For paid subscriptions, we use payment card verification as a proxy for parental 
                identity (parent must have payment card in their name)</li>
                <li><strong>Video Conference Verification:</strong> Upon request, we can schedule a video call with parent to verify identity 
                using government-issued photo ID</li>
                <li><strong>Form Submission with Photo ID:</strong> Parent submits signed consent form along with photo of government-issued 
                ID (ID information is immediately deleted after verification)</li>
              </ul>
              <p className="mb-4 font-semibold">Information Provided to Parents During Consent:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Types of personal information collected from child (conversation data, mood tracking, biometric data if WHOOP connected)</li>
                <li>How information will be used (AI features, crisis detection, personalised insights)</li>
                <li>Whether information will be disclosed to third parties (AI providers, payment processors)</li>
                <li>Parent's right to review child's personal information and request deletion</li>
                <li>Parent's right to refuse further collection or use of child's information</li>
              </ul>
              <p className="mb-4">
                <strong>Parental Access Rights:</strong> Parents/guardians can contact support@therappi.app at any time to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Review all personal information collected from their child</li>
                <li>Request deletion of child's account and all associated data</li>
                <li>Refuse permission for further collection or use of child's information</li>
                <li>Modify consent settings or age verification details</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">11. Updates to this Privacy Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will post any changes on our website and in our
              application. We will also notify you of any material changes by email or other means.
            </p>

            <p className="mb-4">
              Your continued use of the Service after the effective date of any changes constitutes your acceptance of
              the new Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">12. Contact Us</h2>
            <p className="mb-4">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>

            <div className="bg-muted/30 rounded-lg p-4 mb-4">
              <p className="font-semibold">Therappi Ltd</p>
              <p>Thames House, 77-77a High Street</p>
              <p>Esher, Surrey, KT10 9PA, United Kingdom</p>
              <p>Email: support@therappi.app</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">13. Data Protection Officer</h2>
            <p className="mb-4">
              We have appointed a Data Protection Officer (DPO) who is responsible for overseeing our data protection
              practices. You can contact our DPO at:
            </p>

            <div className="bg-muted/30 rounded-lg p-4 mb-4">
              <p className="font-semibold">Data Protection Officer</p>
              <p>Email: support@therappi.app</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">14. Governing Law</h2>
            <p className="mb-4">
              This Privacy Policy shall be governed by and construed in accordance with the laws of England and Wales.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
